import React from "react";
import { createPortal } from "react-dom";
import { trapFocus, isActiveFocusTrap, generateId } from "../../utils/accessibility";
import { MS, usePresence } from "./motion";

// Where focus lands when a dialog opens: an element marked data-autofocus,
// else the first enabled form field, else the dialog itself (never the
// close button, so no loud ring sits on X at open).
const INITIAL_FOCUS =
  '[data-autofocus], input:not([type="hidden"]):not(:disabled):not([readonly]), select:not(:disabled), textarea:not(:disabled):not([readonly])';

const focusInitial = (container) => {
  if (!container) return;
  const target = container.querySelector(INITIAL_FOCUS);
  if (target && typeof target.focus === "function") {
    target.focus({ preventScroll: true });
  }
};

/**
 * Dialog Component
 *
 * A modal dialog component for displaying content that requires user interaction.
 * Includes variations for full dialog, content area, header, title, description and footer.
 *
 * WCAG Compliant with proper ARIA attributes, focus management, and keyboard navigation
 */

export const Dialog = ({
  children,
  open = false,
  onOpenChange,
  className = "max-w-lg",
  ariaLabel,
  ariaDescribedBy,
  ...props
}) => {
  // Generate unique IDs for ARIA relationships
  const dialogId = React.useRef(generateId("dialog"));
  const titleId = React.useRef(generateId("dialog-title"));
  const descId = React.useRef(generateId("dialog-desc"));

  // A dialog opened inside another is already in the parent's portal; it
  // renders in place so the focus traps still see it as a descendant.
  const nested = React.useContext(DialogNestingContext);

  // Refs for focus management
  const dialogRef = React.useRef(null);
  const onOpenChangeRef = React.useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;

  // Stay mounted through the exit (scale .98 + fade, 140 ms on the exit
  // curve) so closing is a movement, not a cut.
  const presence = usePresence(open, { exitMs: MS.exitSlow });

  React.useEffect(() => {
    if (open && dialogRef.current) {
      const release = trapFocus(dialogRef.current, {
        onEscape: () => onOpenChangeRef.current?.(false),
        restoreFocus: true,
      });
      focusInitial(dialogRef.current);
      return release;
    }
  }, [open]);

  // Handle close
  const handleClose = () => {
    if (isActiveFocusTrap(dialogRef.current) && onOpenChange) {
      onOpenChange(false);
    }
  };

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };


  if (!presence.mounted) return null;

  // Pass context to children for ARIA IDs
  const dialogContext = {
    titleId: titleId.current,
    descId: descId.current,
  };

  // Portaled to <body> like Modal: rendered in place, the scrim would sit
  // inside .sg-main's stacking context and the sidebar, top bar and bulk
  // bars would stay bright and clickable above it.
  const layer = (
    <div
      className="sg-scrim"
      onClick={presence.isClosing ? undefined : handleBackdropClick}
      role="presentation"
      data-state={presence.state}
      {...props}
    >
      <div
        ref={dialogRef}
        id={dialogId.current}
        role="dialog"
        aria-modal="true"
        aria-labelledby={ariaLabel ? undefined : titleId.current}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy || descId.current}
        tabIndex={-1}
        data-state={presence.state}
        onAnimationEnd={presence.onAnimationEnd}
        className={`sg-dialog overflow-hidden w-full max-h-[calc(100dvh_-_clamp(16px,11vh,120px)_-_16px)] flex flex-col ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        <DialogNestingContext.Provider value>
          <DialogContext.Provider value={dialogContext}>
            {children}
          </DialogContext.Provider>
        </DialogNestingContext.Provider>
      </div>
    </div>
  );
  return nested ? layer : createPortal(layer, document.body);
};

// Create context for passing ARIA IDs
const DialogContext = React.createContext({});
const DialogNestingContext = React.createContext(false);

export const DialogContent = ({ children, className = "", ...props }) => {
  return (
    <div className={`px-6 py-5 flex-1 overflow-y-auto ${className}`} {...props}>
      {children}
    </div>
  );
};

export const DialogHeader = ({
  children,
  className = "",
  onClose,
  ...props
}) => {
  return (
    <div
      className={`flex items-center justify-between px-6 py-4 flex-shrink-0 border-b border-sg-line ${className}`}
      {...props}
    >
      <div className="flex-1 min-w-0">{children}</div>
      {onClose && (
        <button
          type="button"
          className="w-11 h-11 -my-2 -mr-2 flex items-center justify-center rounded-md text-sg-ink-3 hover:text-sg-ink hover:bg-sg-fill-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--sg-focus)] transition-colors duration-[120ms] ml-3 flex-shrink-0"
          onClick={onClose}
          aria-label="Close"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      )}
    </div>
  );
};

export const DialogTitle = ({ children, className = "", ...props }) => {
  const context = React.useContext(DialogContext);

  return (
    <h2
      id={context.titleId}
      className={`text-[15px] font-semibold text-sg-ink ${className}`}
      {...props}
    >
      {children}
    </h2>
  );
};

export const DialogDescription = ({ children, className = "", ...props }) => {
  const context = React.useContext(DialogContext);

  return (
    <p
      id={context.descId}
      className={`text-sm text-sg-ink-2 ${className}`}
      {...props}
    >
      {children}
    </p>
  );
};

export const DialogFooter = ({ children, className = "", ...props }) => {
  return (
    <div
      className={`px-6 py-4 flex-shrink-0 border-t border-sg-line ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};

export default Dialog;
